import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AddLinkIcon from '@mui/icons-material/AddLink';
import ScreenSearchDesktopIcon from '@mui/icons-material/ScreenSearchDesktop';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import { Box, Button, Grid, IconButton, Modal, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import mixpanel from '../../../../utils/mixpanel';
import { useHistory } from 'react-router-dom';
import { uploadCsvFile, addCollabProfileLinks, colabImportStatus } from 'api_handlers/planner2';

function AddInfluencerModal({
  open,
  close,
  planPlatform,
  planUUID,
  bulkUploadingStatus,
  downloadReport,
  statusData,
  onCreditShortfall,
}) {
  const [openUploadCsvModal, setOpenUploadCsvModal] = useState(false);
  const [openAddBulkModal, setOpenAddBulkModal] = useState(false);
  const [instaSelected, setInstaSelected] = useState(false);
  const [ytSelected, setYtSelected] = useState(false);
  const [text, setText] = useState('');
  const [instaLinks, setInstaLinks] = useState([]);
  const [ytLinks, setYtLinks] = useState([]);
  const [file, setFile] = useState(null);
  const [platform, setPlatform] = useState(
    planPlatform === 'instagram' ? 'instagram' : planPlatform === 'youtube' ? 'youtube' : '',
  );
  const history = useHistory();

  // const [statusData, setStatusData] = useState({
  //   total_count: 0,
  //   success_count: 0,
  //   failed_count: 0,
  // });

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
    }
  };

  const handleDiscoverClick = () => {
    if (process.env.NODE_ENV === 'production') {
      mixpanel.track('collab_list_add_influencer');
    }
    history.push('/planner/discover');
  };

  // api to upload csv. Returns true only when the import actually started,
  // so callers know whether it is safe to show the "Uploading..." toast.
  const handleCsvUpload = async (csvFile) => {
    if (!csvFile) {
      alert('Please select a file to upload.');
      return false;
    }
    try {
      await uploadCSVFiles(csvFile);
      setOpenUploadCsvModal(false);
      return true;
    } catch (error) {
      // A credit shortfall (402) is already surfaced to the user via
      // onCreditShortfall inside uploadCSVFiles - the modal stays open so
      // they can see it. Any other failure just logs, same as before.
      console.error('Error uploading CSV file:', error);
      return false;
    }
  };

  // Starts the import-status poller only once the import actually starts
  // (HTTP 202). On a credit shortfall (402) the poller never starts and the
  // rejection is surfaced to the caller so the modal can stay open instead
  // of showing a bogus "Uploading..." toast for an import that never ran.
  const uploadCSVFiles = async (csvFile) => {
    try {
      const response = await uploadCsvFile(planUUID, csvFile);
      if (response.status === 202) {
        const interval = setInterval(() => {
          importStatus(interval);
        }, 2000);
      }
      return response;
    } catch (error) {
      if (error?.response?.status === 402) {
        onCreditShortfall?.(error.response.data);
      }
      throw error;
    }
  };

  const collabBulkLink = async () => {
    try {
      const response = await addCollabProfileLinks(planUUID, instaLinks, ytLinks);
      if (response.status === 202) {
        const interval = setInterval(() => {
          importStatus(interval);
        }, 2000);
      }
      return response;
    } catch (error) {
      if (error?.response?.status === 402) {
        onCreditShortfall?.(error.response.data);
      }
      throw error;
    }
  };

  // Generate & download a sample sheet with the exact headers the importer
  // expects, so users always upload a matching format.
  const downloadSampleSheet = () => {
    const rows = [
      ['Instagram URL / Handle', 'Youtube URL / Channel Id'],
      ['https://www.instagram.com/nike', ''],
      ['@nike', ''],
      ['', 'https://www.youtube.com/@nike'],
      ['', 'UCq-Fj5jknLsUf-MWSy4_brA'],
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'collab_list_sample.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // api to bulk upload link. Returns true only when the import actually
  // started, so callers know whether it is safe to show the "Uploading..."
  // toast.
  const handleLinkUpload = async () => {
    if (!instaLinks.length && !ytLinks.length) {
      alert('Please add links to upload.');
      return false;
    }
    try {
      await collabBulkLink();
      setOpenAddBulkModal(false);
      return true;
    } catch (error) {
      // A credit shortfall (402) is already surfaced via onCreditShortfall
      // inside collabBulkLink - the modal stays open so the user can see
      // the alert and adjust their selection instead of it vanishing.
      console.error('Error uploading links:', error);
      return false;
    }
  };

  // api to check the progress
  const importStatus = async (interval) => {
    try {
      const response = await colabImportStatus(planUUID);
      // console.log('importStatus', response);
      if (response.total_count === response.success_count + response.failed_count) {
        clearInterval(interval);
      }
      statusData({
        total_count: response.total_count || 0,
        success_count: response.success_count || 0,
        failed_count: response.failed_count || 0,
      });
      // setStatusData({
      //   total_count: response.total_count || 0,
      //   success_count: response.success_count || 0,
      //   failed_count: response.failed_count || 0,
      // });
    } catch (error) {
      console.error('Error fetching plan import status:', error);
    }
  };

  const handleOnChange = (event) => {
    if (platform === 'instagram') {
      const value = event.target.value;
      setText(value);
      const link = value.split(`\n`);
      setInstaLinks(link);
    } else if (platform === 'youtube') {
      const value = event.target.value;
      setText(value);
      const link = value.split(`\n`);
      setYtLinks(link);
    }
  };

  
  return (
    <Grid container>
      <Modal
        open={open}
        //   onClose={handleAddInfluencersClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 500,
            bgcolor: 'background.paper',
          }}
        >
          {!openUploadCsvModal && !openAddBulkModal ? (
            <Grid container width={'100%'}>
              <Grid item width={'100%'} sx={{ borderBottom: '1px solid #f0f0f0', p: '12px' }}>
                <Grid
                  container
                  // justifyContent={'center'}
                  alignItems={'center'}
                  spacing={1}
                >
                  <Grid item md={1} sx={{ ml: '8px' }}>
                    <PersonAddIcon></PersonAddIcon>
                  </Grid>
                  <Grid item md={9} sx={{ fontSize: 17, fontWeight: 700 }}>
                    Add Influencers
                  </Grid>
                  <Grid item md={1}>
                    <IconButton onClick={() => close(false)}>
                      <CloseIcon></CloseIcon>
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item width={'100%'} sx={{ p: '18px' }}>
                <Grid container justifyContent={'center'} alignItems={'center'}>
                  <Grid item sx={{ background: '#f9f9fa', width: '100%', p: '4px' }}>
                    <Grid
                      container
                      justifyContent={'center'}
                      alignItems={'center'}
                      // spacing={}
                    >
                      <Grid item md={1}>
                        <UploadFileIcon sx={{ mt: '4px' }}></UploadFileIcon>
                      </Grid>
                      <Grid item md={8}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                          Upload CSV,XLS,XLSX
                        </Typography>
                      </Grid>
                      <Grid item md={3}>
                        <Button
                          variant="contained"
                          sx={{ textTransform: 'none', height: '32px' }}
                          onClick={() => setOpenUploadCsvModal(true)}
                        >
                          Add Now
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item sx={{ background: '#f9f9fa', width: '100%', p: '4px', mt: '6px' }}>
                    <Grid
                      container
                      justifyContent={'center'}
                      alignItems={'center'}
                      // spacing={}
                    >
                      <Grid item md={1}>
                        <AddLinkIcon sx={{ mt: '4px' }}></AddLinkIcon>
                      </Grid>
                      <Grid item md={8}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                          Import Bulk Links
                        </Typography>
                      </Grid>
                      <Grid item md={3}>
                        <Button
                          variant="contained"
                          sx={{ textTransform: 'none', height: '32px' }}
                          onClick={() => setOpenAddBulkModal(true)}
                        >
                          Add Now
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item sx={{ background: '#f9f9fa', width: '100%', p: '4px', mt: '6px' }}>
                    <Grid container justifyContent={'center'} alignItems={'center'}>
                      <Grid item md={1}>
                        <ScreenSearchDesktopIcon sx={{ mt: '4px' }}></ScreenSearchDesktopIcon>
                      </Grid>
                      <Grid item md={8}>
                        <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                          Add From Discover
                        </Typography>
                      </Grid>
                      <Grid item md={3}>
                        <Button
                          variant="contained"
                          sx={{ textTransform: 'none', height: '32px' }}
                          onClick={handleDiscoverClick}
                        >
                          Add Now
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ) : openUploadCsvModal ? (
            <Grid container width={'100%'}>
              <Grid item width={'100%'} sx={{ borderBottom: '1px solid #f0f0f0', p: '12px' }}>
                <Grid container alignItems={'center'}>
                  <Grid item md={11}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 600 }}>
                      Upload Influencers
                    </Typography>
                  </Grid>
                  <Grid item md={1}>
                    <IconButton onClick={() => close(false)}>
                      <CloseIcon></CloseIcon>
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item width={'100%'} height={300} sx={{ p: '18px' }}>
                <Grid
                  container
                  height={200}
                  sx={{ border: '1px dashed #454545', borderRadius: 1 }}
                  direction={'column'}
                  //   justifyContent={'center'}
                  alignItems={'center'}
                >
                  <Grid item>
                    <CloudUploadIcon sx={{ height: 50 }}></CloudUploadIcon>
                  </Grid>
                  <Grid item>
                    <Typography sx={{ fontSize: 20 }}>Drop files here</Typography>
                  </Grid>
                  <Grid item>
                    <Typography sx={{ mt: 1, color: '#595b5f' }}>
                      Supported format: CSV, XLS, XLSX
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography sx={{ mt: 2, fontWeight: 600 }}>OR</Typography>
                  </Grid>

                  <Grid item>
                    <Typography
                      sx={{ mt: 2, color: '#febd1c', cursor: 'pointer' }}
                      onClick={() => {
                        document.getElementById('file-input').click();
                      }}
                    >
                      Browse files
                    </Typography>
                    <input
                      type="file"
                      id="file-input"
                      accept=".xls,.xlsx,.csv" // Only allow Excel files
                      style={{ display: 'none' }}
                      onChange={handleFileChange}
                    />
                  </Grid>
                  <Grid item>
                    <Typography>{file ? file.name : ''}</Typography>
                  </Grid>
                </Grid>
                <Grid item>
                  <Grid container sx={{ mt: 1 }}>
                    <Grid item>
                      <Typography sx={{ fontSize: '11px', color: '#707070',width:'87%' }}>
                        Note - Please download the sample sheet and upload the same. Columns
                        accepted: <span style={{fontWeight:700,color:'#333'}}>Instagram URL / Handle</span> and
                        <span style={{fontWeight:700,color:'#333'}}> Youtube URL / Channel Id</span>. You can add upto
                        <span style={{fontWeight:700,color:'#333'}}> 100 influencers</span> at a time.
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="outlined"
                        endIcon={<SystemUpdateAltIcon></SystemUpdateAltIcon>}
                        sx={{
                          textTransform: 'none',
                          color: '#000',
                          borderColor: '#d8dbe0',
                          mt: 1,
                          height: 30,
                        }}
                        onClick={downloadSampleSheet}
                      >
                        Sample Sheet
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item width={'100%'} sx={{ p: '18px' }}>
                <Grid container justifyContent={'flex-end'}>
                  <Grid item>
                    <Button
                      sx={{ color: '#a0a0a0', width: '100px' }}
                      onClick={() => setOpenUploadCsvModal(false)}
                    >
                      Back
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="contained"
                      sx={{ width: '100px' }}
                      onClick={async () => {
                        const started = await handleCsvUpload(file);
                        if (started) {
                          bulkUploadingStatus(true);
                        }
                      }}
                    >
                      Upload
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            ''
          )}
          {openAddBulkModal ? (
            <Grid container width={'100%'} direction={'column'} sx={{ p: '12px' }}>
              <Grid item width={'100%'}>
                <Grid container alignItems={'center'}>
                  <Grid item md={11}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 600, ml: 2 }}>
                      Add Bulk Links
                    </Typography>
                  </Grid>
                  <Grid item md={1}>
                    <IconButton onClick={() => close(false)}>
                      <CloseIcon></CloseIcon>
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item sx={{ mt: '10px' }}>
                <Grid container alignItems={'center'} spacing={2}>
                  <Grid item sx={{ ml: 2, p: '2px' }}>
                    <Button
                      onClick={() => {
                        setInstaSelected(true);
                        setInstaLinks([]);
                        setYtLinks([]);
                        setText('');
                        setYtSelected(false);
                        setPlatform('instagram');
                      }}
                      variant="contained"
                      sx={
                        instaSelected || planPlatform === 'instagram'
                          ? { width: '120px' }
                          : { width: '120px', background: '#dedede', color: '#000' }
                      }
                      startIcon={<InstagramIcon></InstagramIcon>}
                      disabled={planPlatform === 'youtube' ? true : false}
                    >
                      Instagram
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      onClick={() => {
                        setYtSelected(true);
                        setInstaSelected(false);
                        setInstaLinks([]);
                        setYtLinks([]);
                        setText('');
                        setPlatform('youtube');
                      }}
                      variant="contained"
                      sx={
                        ytSelected || planPlatform === 'youtube'
                          ? { width: '120px' }
                          : { width: '120px', background: '#dedede', color: '#000' }
                      }
                      startIcon={<YouTubeIcon></YouTubeIcon>}
                      disabled={planPlatform === 'instagram' ? true : false}
                    >
                      YouTube
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item sx={{ p: '18px' }}>
                <Grid
                  container
                  sx={{ border: '1px dashed #454545', borderRadius: 1, height: 200, p: '18px' }}
                  direction="column"
                >
                  <Grid item>
                    <Typography>Paste your links here, one link per line</Typography>
                  </Grid>
                  <Grid item>
                    <TextField
                      multiline
                      rows={5}
                      placeholder={
                        planPlatform === 'instagram' || instaSelected
                          ? `https://www.instagram.com/username1\nhttps://www.instagram.com/username2\nhttps://www.instagram.com/username3`
                          : planPlatform === 'youtube' || ytSelected
                          ? `https://www.youtube.com/username1\nhttps://www.youtube.com/username2\nhttps://www.youtube.com/username3`
                          : `Please select a platform.`
                      }
                      sx={{
                        '& textarea': {
                          alignSelf: 'self-start',
                        },
                        overflowX: 'auto',
                      }}
                      style={{
                        width: '100%',
                        height: 140,
                      }}
                      value={instaLinks ? text : text}
                      onChange={handleOnChange}
                    ></TextField>
                  </Grid>
                </Grid>
                <Grid item>
                  <Typography sx={{ fontSize: 12, mt: 1 }}>
                    Note - You can add upto <span style={{fontWeight:700,color:'#333'}}>100 influencers</span> at a time for the selected platform.
                  </Typography>
                </Grid>
                <Grid item width={'100%'} sx={{ mt: '18px' }}>
                  <Grid container justifyContent={'flex-end'}>
                    <Grid item>
                      <Button
                        sx={{ color: '#a0a0a0', width: '100px' }}
                        onClick={() => setOpenAddBulkModal(false)}
                      >
                        Back
                      </Button>
                    </Grid>
                    <Grid item>
                      <Button
                        variant="contained"
                        sx={{ width: '100px' }}
                        onClick={async () => {
                          setInstaLinks([]);
                          setYtLinks([]);
                          setText('');
                          const started = await handleLinkUpload();
                          if (started) {
                            bulkUploadingStatus(true);
                          }
                        }}
                      >
                        Add
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            ''
          )}
        </Box>
      </Modal>
    </Grid>
  );
}

export default AddInfluencerModal;
